import React from "react"
import { graphql } from "gatsby"
import styled from "@emotion/styled"
import { GatsbyImage } from "gatsby-plugin-image"

import { mapperWithFunction } from "../common/utils"
import { HeroVideo, theme } from "@singita/components"
import { useSetNavbarStylesOnPresence } from "gatsby-singita-theme/src/hooks"
import { TEMPLATE_TYPES } from "gatsby-singita-theme/src/common/constants"
import { useTranslation } from "react-i18next"
import { getSrc } from "gatsby-plugin-image"
import { generateVideoSchema } from 'gatsby-singita-theme/src/utils/schemaUtils'
import { useRegisterSchema } from 'gatsby-singita-theme/src/context/SchemaContext'

const StyledBackground = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
`

const HERO_VIDEO_MAP = {
  headline: ["headline"],
  subtitle: ["subtitle"],
  fullUrl: ["video", "fullUrl"],
  previewUrl: ["video", "previewUrl"],
  renderThumbnail: value => () => {
    return value.video.thumbnail ? (
      <StyledBackground
        image={value.video.thumbnail.gatsbyImageData}
        loading="eager"
      />
    ) : null
  },
  showPattern: ["showPattern"],
  colorScheme: ["colorScheme"],
  thumbnailUrl: ({ video }) => {
    const thumbnail = video?.thumbnail
    if (!thumbnail) return null

    // Handle both cases: thumbnail as parent object or direct gatsbyImageData
    const imageData = thumbnail.gatsbyImageData || thumbnail

    // Try getSrc first
    const srcFromGetSrc = getSrc(imageData)
    if (srcFromGetSrc) return srcFromGetSrc

    // Fallback: manually extract from gatsbyImageData structure
    if (imageData?.images?.fallback?.src) {
      return imageData.images.fallback.src
    }

    // Last resort: extract from sources srcSet
    if (imageData?.images?.sources?.[0]?.srcSet) {
      const srcSet = imageData.images.sources[0].srcSet
      const firstUrl = srcSet.split(',')[0].trim().split(' ')[0]
      return firstUrl
    }

    return null
  },
  caption: ({ subtitle, caption }) => {
    return caption || subtitle || undefined
  },
  description: ({ video, subtitle, caption, headline }) => {
    // Prioritize Vimeo description, then fall back to caption, subtitle, or headline
    return video?.vimeoMeta?.description || caption || subtitle || headline || undefined
  },
  uploadDate: ["video", "createdAt"],
}

const HeroVideoWrapper = props => {
  const { t } = useTranslation()
  const heroRef = useSetNavbarStylesOnPresence(0, "-50px 0px 0px 0px", {
    logoColor: theme.colors.textWhite,
    secondaryButtonColor: theme.colors.textWhite,
  })
  const heroVideoProps = mapperWithFunction(props, HERO_VIDEO_MAP, TEMPLATE_TYPES.VIDEO)
  
  // Generate and register video schema
  const videoSchema = React.useMemo(() => {
    return generateVideoSchema({
      title: heroVideoProps.headline,
      headline: heroVideoProps.headline,
      description: heroVideoProps.description,
      caption: heroVideoProps.caption,
      embedUrl: heroVideoProps.previewUrl || heroVideoProps.fullUrl,
      thumbnailUrl: heroVideoProps.thumbnailUrl,
      uploadDate: heroVideoProps.uploadDate,
    });
  }, [heroVideoProps]);

  useRegisterSchema(videoSchema);
  
  return (
    <HeroVideo
      {...heroVideoProps}
      ref={heroRef}
      patternProps={props.patternProps}
      headlineSx={{ textAlign: "center" }}
      buttonText={t("hero.play")}
    />
  )
}

export default HeroVideoWrapper

export const heroVideoFields = graphql`
  fragment HeroVideoFields on ContentfulLayoutHeroVideo {
    id
    headline
    subtitle
    showPattern
    video {
      caption
      headline
      fullUrl
      previewUrl
      thumbnail {
        gatsbyImageData(
          placeholder: NONE
          layout: CONSTRAINED
          quality: 95
          width: 1900
          formats: [AUTO, WEBP]
        )
      }
      createdAt
      vimeoMeta {
        description
        title
        duration
        width
        height
        authorName
        uploadDate
      }
    }
  }
`